//
// Copyright (c) 2019-Present Syncari - All rights reserved.
// Filter v2-only redesign of the right value's data token picker: a compact icon
// trigger + a flat, searchable token list (as opposed to the shared TokenSelector's
// "+ Data token" text trigger and categorized chip list used everywhere else).
//
import Popover from 'antd/lib/popover';
import cx from 'classnames';
import { useCombobox } from 'downshift';
import { matchSorter } from 'match-sorter';
import { useMemo, useRef, useState } from 'react';

import { ReactComponent as TokenSelectorIcon } from 'assets/icons/token-selector.svg';
import { useI18nContext, withI18n } from 'components/I18nProvider';
import Input, { InputRef } from 'components/inputs/Input';
import Spinner from 'components/Spinner';
import { useTokensForSelectedNode } from 'store/tokens/hooks';
import { Token } from 'store/tokens/types';

import './FilterTokenSelector.less';

const tokenToString = (token: Token | null) => (token ? token.shortLabel : '');

type FilterTokenSelectorMenuProps = {
  tokens: Token[];
  onRequestClose: () => void;
  onTokenSelect: (token: Token) => void;
};

const FilterTokenSelectorMenu = ({ tokens, onRequestClose, onTokenSelect }: FilterTokenSelectorMenuProps) => {
  const { tn } = useI18nContext();
  const inputRef = useRef<InputRef | null>(null);
  const [inputValue, setInputValue] = useState('');

  const filteredTokens = useMemo(() => matchSorter(tokens, inputValue, { keys: ['shortLabel', 'label'] }), [
    tokens,
    inputValue,
  ]);

  const handleTokenSelect = (token: Token) => {
    setInputValue('');
    onRequestClose();
    onTokenSelect(token);
  };

  const { getComboboxProps, getMenuProps, getInputProps, getItemProps, highlightedIndex } = useCombobox({
    items: filteredTokens,
    inputValue,
    onInputValueChange: ({ inputValue }) => setInputValue(inputValue ?? ''),
    itemToString: tokenToString,
    onSelectedItemChange: ({ selectedItem }) => {
      selectedItem && handleTokenSelect(selectedItem);
    },
  });

  if (tokens.length === 0) {
    return <div className="filter-token-selector-empty">{tn('no_tokens_available')}</div>;
  }

  return (
    <div className="filter-token-selector-panel">
      <div className="filter-token-selector-search" {...getComboboxProps()}>
        <Input
          autoFocus
          allowClear
          onClear={() => {
            setInputValue('');
            inputRef.current?.focus();
          }}
          {...getInputProps({
            placeholder: tn('search_tokens_placeholder'),
            // @ts-expect-error: error because of the antd input
            ref: inputRef,
          })}
        />
      </div>
      <ul className="filter-token-selector-list" {...getMenuProps()}>
        {filteredTokens.map((token, index) => (
          <li
            key={token.value}
            className={cx('filter-token-selector-item', { highlighted: highlightedIndex === index })}
            {...getItemProps({ item: token, index })}>
            <span className="filter-token-selector-item-label">{`{{ ${token.shortLabel} }}`}</span>
            <span className="filter-token-selector-item-type">{token.datatype}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export interface FilterTokenSelectorProps {
  onTokenSelect: (token: Token) => void;
}

const FilterTokenSelector = ({ onTokenSelect }: FilterTokenSelectorProps) => {
  const { tn } = useI18nContext();
  const [showing, setShowing] = useState(false);
  const { tokens = {}, isLoading, isFetching } = useTokensForSelectedNode();

  const allTokens = useMemo(() => Object.values(tokens).flat(), [tokens]);

  const close = () => setShowing(false);

  return (
    <Popover
      trigger="click"
      placement="bottomRight"
      getPopupContainer={() => document.body}
      visible={showing}
      onVisibleChange={setShowing}
      overlayClassName="filter-token-selector-popover-overlay"
      content={
        showing &&
        (isLoading || isFetching ? (
          <div className="filter-token-selector-panel filter-token-selector-panel--loading">
            <Spinner />
          </div>
        ) : (
          <FilterTokenSelectorMenu tokens={allTokens} onRequestClose={close} onTokenSelect={onTokenSelect} />
        ))
      }>
      <button
        type="button"
        aria-label={tn('insert_token_trigger_label')}
        aria-expanded={showing}
        className={cx('filter-token-selector-trigger', { active: showing })}>
        <TokenSelectorIcon width={16} height={16} />
      </button>
    </Popover>
  );
};

export default withI18n(FilterTokenSelector, 'Tokens');
